import React, { useRef, useEffect, useState, useCallback } from 'react'
import { chart as rawChart, getOptionsConfig, getDefaultOptionsValues } from '@rawgraphs/rawgraphs-core'
import useDebounce from '../../hooks/useDebounce'
import WarningMessage from '../WarningMessage'
import { onChartRendered } from '../../gaEvents'
import { annotateColorDomainAttributes } from '../../services/colorDomainAttributes'
import { getBumpLegendOrder } from '../../services/legendLayout'
import { applySvgTransformPipeline } from '../../services/svgTransformPipeline'
import { normalizeChartMapping } from '../../services/normalizeChartMapping'
import { CHART_IDS } from '../../charts/chartIds'
import {
  renderBeeswarmCanvas,
  renderPointMapCanvas,
} from '../../services/canvasPreview'
import {
  findChartDatumFromNode,
  summarizeChartDatum,
} from '../../services/chartDatumDetails'
import { suggestMarginAdjustments } from '../../services/chartAutoMargin'
import { getChartLayoutDiagnostics } from '../../services/chartLayoutDiagnostics'

function normalizePreviewSvg(svgNode) {
  if (!svgNode) return
  svgNode.style.width = '100%'
  svgNode.style.maxWidth = '100%'
  svgNode.style.height = 'auto'
  svgNode.style.display = 'block'
  svgNode.style.overflow = 'visible'
}


const ChartPreview = ({
  chart,
  dataset: data,
  dataTypes,
  mapping,
  visualOptions,
  brandTheme,
  chartColorSyncMode = 'brand',
  hasMappedColorField = false,
  datasetRowCount = 0,
  previewRendererMode = { mode: 'svg', message: null },
  loading = false,
  loadingMessage = 'Updating preview…',
  error,
  mappingError,
  setError,
  setRawViz,
  mappedData,
  className,
  renderSource = 'unknown',
  onAutoMargin = null,
  onLayoutDiagnostics = null,
  useDeclaredSvgViewport = false,
}) => {
  const domRef = useRef(null)
  const canvasRef = useRef(null)
  const stageRef = useRef(null)
  const [selectedDatum, setSelectedDatum] = useState(null)
  const [previewNoticeDismissed, setPreviewNoticeDismissed] = useState(false)
  const [renderLoading, setRenderLoading] = useState(false)
  const isCanvasPreview = previewRendererMode?.mode === 'canvas'
  const hasNativeChartColorScale = Boolean(
    getOptionsConfig(chart?.visualOptions)?.colorScale
  )
  const colorDimension = getOptionsConfig(chart?.visualOptions)?.colorScale?.dimension || null
  const shouldRecolorMarks = !hasNativeChartColorScale

  // Ensure every option the chart declares has a value. Saved projects or
  // chart-type switches can leave options undefined; undefined numeric options
  // propagate as NaN into d3 layouts (e.g. pack padding), causing broken renders.
  const chartOptionDefaults = React.useMemo(
    () => (chart?.visualOptions ? getDefaultOptionsValues(getOptionsConfig(chart.visualOptions)) : {}),
    [chart]
  )
  const normalizedMapping = React.useMemo(
    () => normalizeChartMapping(chart, mapping, dataTypes),
    [chart, mapping, dataTypes]
  )
  const legendOrder = React.useMemo(
    () =>
      chart?.metadata?.id === CHART_IDS.BUMP_CHART
        ? getBumpLegendOrder(mappedData, mapping)
        : null,
    [chart, mappedData, mapping]
  )
  const previewNoticeTitle = isCanvasPreview
    ? 'Fast preview mode'
    : previewRendererMode?.previewSampled
      ? 'Sampled preview mode'
      : 'Preview mode'

  const vizOptionsDebounced = useDebounce(visualOptions, 200)

  const closeDatumPopup = useCallback(() => {
    setSelectedDatum(null)
  }, [])

  const handlePreviewClick = useCallback(
    (event) => {
      if (isCanvasPreview) {
        setPreviewNoticeDismissed(true)
        closeDatumPopup()
        return
      }

      const stageNode = stageRef.current
      const svgNode = domRef.current?.querySelector('svg')

      if (!stageNode || !svgNode) {
        closeDatumPopup()
        return
      }

      const datum = findChartDatumFromNode(event.target, svgNode)
      if (!datum) {
        closeDatumPopup()
        return
      }

      const summary = summarizeChartDatum(datum, dataTypes)
      if (!summary) {
        closeDatumPopup()
        return
      }

      const stageRect = stageNode.getBoundingClientRect()
      const x = event.clientX - stageRect.left
      const y = event.clientY - stageRect.top

      setSelectedDatum({
        ...summary,
        left: Math.max(16, Math.min(x + 12, stageRect.width - 312)),
        top: Math.max(16, Math.min(y + 12, stageRect.height - 220)),
      })
    },
    [closeDatumPopup, dataTypes, isCanvasPreview]
  )

  useEffect(() => {
    setSelectedDatum(null)
  }, [chart, mappedData, visualOptions, brandTheme])

  useEffect(() => {
    setPreviewNoticeDismissed(false)
  }, [chart, previewRendererMode?.mode, previewRendererMode?.message, datasetRowCount])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedDatum(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    let frameId = null

    setError(null)

    // control required variables
    // need to create this array because the prop mapping does not return to {} when data is inserted and removed
    const currentlyMapped = []
    for (let variable in mapping) {
      if (mapping[variable].ids && mapping[variable].ids.length > 0) {
        currentlyMapped.push(variable)
      }
    }

    let requiredVariables = chart.dimensions.filter(
      (d) => d.required && currentlyMapped.indexOf(d.id) === -1
    )

    if (requiredVariables.length > 0) {
      let errorMessage = (
        <span>
          Required chart variables: you need to map{' '}
          {requiredVariables
            .map((d, i) => <span key={i} className="font-weight-bold">{d.name}</span>)
            .reduce((prev, curr) => [prev, ' and ', curr])}
        </span>
      )
      setError({ variant: 'secondary', message: errorMessage })
      setRawViz(null)
      setRenderLoading(false)
      while (domRef.current.firstChild) {
        domRef.current.removeChild(domRef.current.firstChild)
      }
      return
    }

    // control multiple required variables
    const multivaluesVariables = chart.dimensions.filter(
      (d) =>
        d.multiple &&
        d.required &&
        d.minValues &&
        mapping[d.id].ids.length < d.minValues
    )
    if (multivaluesVariables.length > 0) {
      let errorMessage = (
        <span>
          Please map{' '}
          {multivaluesVariables
            .map((d) => (
              <>
                at least <span className="font-weight-bold">{d.minValues}</span>{' '}
                dimensions on <span className="font-weight-bold">{d.name}</span>
              </>
            ))
            .reduce((prev, curr) => [prev, ' and ', curr])}
          .
        </span>
      )
      setError({ variant: 'secondary', message: errorMessage })
      setRawViz(null)
      setRenderLoading(false)
      while (domRef.current.firstChild) {
        domRef.current.removeChild(domRef.current.firstChild)
      }
      return
    }

    // control data-types mismatches
    for (let variable in mapping) {
      if (
        mapping[variable].ids &&
        mapping[variable].ids.length > 0 &&
        !mapping[variable].isValid
      ) {
        const variableObj = chart.dimensions.find((d) => d.id === variable)
        const errorMessage = `Data-type mismatch: you can’t map ${mapping[variable].mappedType}s on ${variableObj.name}.`
        setError({ variant: 'danger', message: errorMessage })
        setRawViz(null)
        setRenderLoading(false)
        while (domRef.current.firstChild) {
          domRef.current.removeChild(domRef.current.firstChild)
        }
        return
      }
    }

    if (!mappedData || !data || !dataTypes) {
      setRawViz(null)
      setRenderLoading(false)
      while (domRef.current.firstChild) {
        domRef.current.removeChild(domRef.current.firstChild)
      }
      return
    }

    setRenderLoading(true)
    frameId = window.requestAnimationFrame(() => {
      // Outer frame: let React commit the loading state and the browser paint it.
      // Inner frame: start the heavy synchronous render after that paint.
      frameId = window.requestAnimationFrame(() => {
      if (isCanvasPreview) {
        while (domRef.current.firstChild) {
          domRef.current.removeChild(domRef.current.firstChild)
        }

        try {
          if (chart?.metadata?.id === CHART_IDS.POINT_MAP) {
            renderPointMapCanvas(
              canvasRef.current,
              mappedData,
              vizOptionsDebounced,
              normalizedMapping,
              null,
              brandTheme
            )
          } else if (chart?.metadata?.id === CHART_IDS.BEESWARM) {
            renderBeeswarmCanvas(
              canvasRef.current,
              mappedData,
              vizOptionsDebounced,
              normalizedMapping,
              brandTheme
            )
          }
          setRawViz(null)
          setError(null)
          onChartRendered(chart.metadata, renderSource)
        } catch (e) {
          setError({ variant: 'danger', message: 'Preview error. ' + e.message })
        } finally {
          setRenderLoading(false)
        }
        return
      }

      try {
        const viz = rawChart(chart, {
          data,
          mapping: normalizedMapping,
          dataTypes,
          visualOptions: { ...chartOptionDefaults, ...vizOptionsDebounced },
        })
        try {
          const rawViz = viz.renderToDOM(domRef.current, mappedData)
          const svgNode = rawViz?._node?.firstChild
          annotateColorDomainAttributes(svgNode, chart, colorDimension)
          const declaredWidth = Number.parseFloat(svgNode?.getAttribute('width'))
          const declaredHeight = Number.parseFloat(svgNode?.getAttribute('height'))
          const { xLabelResult, yLabelResult } = applySvgTransformPipeline(svgNode, {
            brandTheme,
            chartId: chart?.metadata?.id,
            mapping: normalizedMapping,
            dataTypes,
            visualOptions: vizOptionsDebounced,
            recolorMarks: shouldRecolorMarks,
            legendOrder,
            // A Canvas card is scaled by its parent object. Measuring every
            // SVG descendant there can produce a viewport many times larger
            // than the actual RAWGraphs artboard, so retain declared bounds.
            svgMetrics: useDeclaredSvgViewport && Number.isFinite(declaredWidth) && Number.isFinite(declaredHeight)
              ? { x: 0, y: 0, width: declaredWidth, height: declaredHeight, hasViewBox: false }
              : null,
            fitToContents: !useDeclaredSvgViewport,
          })
          try {
            svgNode.dataset.lugsailLabelStrategy = JSON.stringify({
              x: xLabelResult ?? null,
              y: yLabelResult ?? null,
            })
          } catch {
            // non-critical
          }
          let marginSuggestions = null
          try {
            marginSuggestions = suggestMarginAdjustments(svgNode, vizOptionsDebounced)
          } catch {
            // Non-critical: diagnostics can still report the finished SVG.
          }
          if (onLayoutDiagnostics) {
            try {
              onLayoutDiagnostics(getChartLayoutDiagnostics(svgNode, {
                xLabelResult,
                yLabelResult,
                marginSuggestions,
              }))
            } catch {
              // Diagnostics must never make the shared chart renderer fail.
            }
          }
          if (onAutoMargin) {
            try {
              if (marginSuggestions) onAutoMargin(marginSuggestions)
            } catch {
              // non-critical
            }
          }
          normalizePreviewSvg(svgNode)
          setRawViz(rawViz)
          setError(null)
          onChartRendered(chart.metadata, renderSource)
        } catch (e) {
          const msg = (e.message || '').replace(/decrase/gi, 'decrease')
          setError({ variant: 'danger', message: 'Chart error. ' + msg })
          setRawViz(null)
        }
      } catch (e) {
        while (domRef.current.firstChild) {
          domRef.current.removeChild(domRef.current.firstChild)
        }
        setError({ variant: 'danger', message: 'Chart error. ' + e.message })
        setRawViz(null)
      } finally {
        setRenderLoading(false)
      }
      }) // end inner rAF
    }) // end outer rAF

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setError, vizOptionsDebounced, setRawViz, mappedData, chart, mapping, normalizedMapping, brandTheme, chartColorSyncMode, data, dataTypes, hasMappedColorField, isCanvasPreview, legendOrder, shouldRecolorMarks, colorDimension, chartOptionDefaults, useDeclaredSvgViewport])

  return (
    <div className={className || 'col-8 col-xl-9'}>
      <div className="overflow-auto">
        {(error || mappingError) && (
          <WarningMessage
            variant={(error || mappingError).variant}
            message={(error || mappingError).message}
          />
        )}
        <div className="preview-stage" ref={stageRef} onClick={handlePreviewClick}>
          {(loading || renderLoading) && (
            <div className="preview-loading-overlay" aria-live="polite">
              <div className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
              <span>{loadingMessage}</span>
            </div>
          )}
          {previewRendererMode?.message &&
            !previewNoticeDismissed && (
            <div className="preview-mode-note" aria-live="polite">
              <div className="preview-mode-note__header">
                <strong>{previewNoticeTitle}</strong>
                <button
                  type="button"
                  className="preview-mode-note__close"
                  onClick={(event) => {
                    event.stopPropagation()
                    setPreviewNoticeDismissed(true)
                  }}
                  aria-label="Dismiss fast preview notice"
                >
                  ×
                </button>
              </div>
              <span>
                {previewRendererMode.message}
                {previewRendererMode?.previewSampled &&
                previewRendererMode?.previewSampleRows &&
                datasetRowCount > 0
                  ? ` Previewing ${Math.min(
                      datasetRowCount,
                      previewRendererMode.previewSampleRows
                    ).toLocaleString()} sampled rows from ${datasetRowCount.toLocaleString()}.`
                  : datasetRowCount > 0
                    ? ` (${datasetRowCount.toLocaleString()} rows)`
                    : ''}
              </span>
            </div>
          )}
          <canvas
            ref={canvasRef}
            className={`preview-canvas-surface${isCanvasPreview ? ' is-active' : ''}`}
          />
          <div className="preview-render-surface" ref={domRef}>
            {/* Don't put content in this <div /> */}
          </div>
          {selectedDatum && (
            <div
              className="chart-datum-popup"
              style={{ left: selectedDatum.left, top: selectedDatum.top }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="chart-datum-popup__header">
                <strong>{selectedDatum.title}</strong>
                <button
                  type="button"
                  className="chart-datum-popup__close"
                  onClick={closeDatumPopup}
                  aria-label="Close data details"
                >
                  ×
                </button>
              </div>
              <div className="chart-datum-popup__body">
                {selectedDatum.rows.map((row) => (
                  <div key={row.label} className="chart-datum-popup__row">
                    <span className="chart-datum-popup__label">{row.label}</span>
                    <span className="chart-datum-popup__value">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(ChartPreview)
